<template>
  <div class="checkout-wrapper">
    <div class="checkout-container">
      <router-link to="/cart" class="back-link">&larr; {{ translations.checkout.back_to_cart }}</router-link>

      <form class="form-section" @submit.prevent="order">
        <h2>{{ translations.checkout.step_contact }}</h2>
        <div class="two-cols">
          <label>{{ translations.checkout.first_name }} <input type="text" v-model="form.firstName" required /></label>
          <label>{{ translations.checkout.last_name }} <input type="text" v-model="form.lastName" required /></label>
        </div>
        <div class="two-cols">
          <label>{{ translations.checkout.phone }} <input type="tel" v-model="form.phone" required /></label>
          <label>{{ translations.checkout.email }} <input type="email" v-model="form.email" required /></label>
        </div>

        <h2>{{ translations.checkout.step_address }}</h2>
        <div class="two-cols">
          <label>{{ translations.checkout.address }} <input type="text" v-model="form.address" required /></label>
          <label>{{ translations.checkout.city }} <input type="text" v-model="form.city" required /></label>
        </div>
        <div class="two-cols">
          <label>{{ translations.checkout.zip }} <input type="text" v-model="form.zip" required /></label>
          <label>
            {{ translations.checkout.country }}
            <select v-model="form.country">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ countryEndonym(c.code) }}
              </option>
            </select>
          </label>
        </div>

        <label class="checkbox pretty-checkbox">
          <input type="checkbox" v-model="form.wantInvoice" />
          <span class="custom-check"></span>
          {{ translations.checkout.want_invoice }}
        </label>

        <div v-if="form.wantInvoice" class="invoice-section">
          <hr />
          <h3>{{ translations.checkout.step_invoice }}</h3>
          <p class="invoice-hint">{{ translations.checkout.invoice_hint }}</p>

          <label>{{ translations.checkout.invoice_company }}
            <input type="text" v-model="form.invoiceCompany" :required="form.wantInvoice" />
          </label>

          <label>{{ translations.checkout.invoice_nip }}
            <input type="text" v-model="form.invoiceNIP" :required="form.wantInvoice" />
          </label>

          <label>{{ translations.checkout.invoice_address }}
            <input type="text" v-model="form.invoiceAddress" />
          </label>

          <label>{{ translations.checkout.invoice_zip }}
            <input type="text" v-model="form.invoiceZip" />
          </label>

          <label>{{ translations.checkout.invoice_city }}
            <input type="text" v-model="form.invoiceCity" />
          </label>

          <label>{{ translations.checkout.invoice_country }}
            <select v-model="form.invoiceCountry">
              <option v-for="c in availableCountries" :key="c.code" :value="c.code">
                {{ countryEndonym(c.code) }}
              </option>
            </select>
          </label>
        </div>

        <h2>{{ translations.checkout.step_payment }}</h2>
        <div class="payment-box-group">
          <label
            v-for="method in availablePaymentMethods"
            :key="method.id"
            class="payment-box"
            :class="{ selected: form.paymentMethod === method.id }"
          >
            <input type="radio" :value="method.id" v-model="form.paymentMethod" name="payment" />
            <div class="radio-content">
              <span class="dot"></span>
              <span class="method-label">{{ getPaymentMethodName(method) }}</span>
            </div>
          </label>
        </div>

        <h2>{{ translations.checkout.step_terms }}</h2>
        <label class="checkbox pretty-checkbox">
          <input type="checkbox" v-model="form.terms" required />
          <span class="custom-check"></span>
          {{ translations.checkout.terms_accept }}
        </label>

        <button class="btn-submit" :disabled="processing">
          {{ processing ? translations.checkout.processing : translations.checkout.order_btn }}
        </button>
      </form>

      <aside class="summary-section">
        <h3>{{ translations.checkout.summary }}</h3>
        <ul v-if="cart.length">
          <li v-for="item in cart" :key="item.id + '-' + (item.variantKey || '-')" class="cart-item">
            <div class="cart-img-container">
              <img :src="item.image" class="cart-img" />
            </div>
            <div>
              <strong>{{ item.name }}</strong>
              <div class="variant" v-if="item.variant">{{ getVariantName(item.variant) }}</div>
              <div>
                {{ Number(item.price).toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }} × {{ item.quantity }}
              </div>
            </div>
          </li>
        </ul>
        <div v-else class="empty">{{ translations.checkout.empty_cart }}</div>
        <hr />
        <div class="summary-row">
          {{ translations.checkout.delivery }}:
          <span>
            {{ shippingTotal.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}
            <small v-if="freeShippingActive" style="color:#22c55e;">({{ translations.cart?.free_shipping_note || "darmowa wysyłka" }})</small>
          </span>
        </div>
        <div class="summary-total">
          {{ translations.checkout.total }}:
          <strong>{{ totalWithShipping.toFixed(2) }} {{ getCurrencySymbol(currentCurrency) }}</strong>
        </div>
      </aside>
    </div>
  </div>
</template>

<script>
import { translations, currentLang } from '@/locales/lang'
import { currentCurrency } from '@/composables/currency'
import { watch } from 'vue'

function getCurrencySymbol(currency) {
  const symbols = { PLN: 'zł', EUR: '€', USD: '$' }
  return symbols[currency] || currency
}

export default {
  name: "AppCheckout",
  data() {
    return {
      cart: [],
      availableCountries: [],
      availablePaymentMethods: [],
      processing: false,
      unwatchCurrency: null,
      form: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        address: "",
        city: "",
        zip: "",
        country: "PL",
        paymentMethod: "",
        terms: false,
        wantInvoice: false,
        invoiceCompany: "",
        invoiceNIP: "",
        invoiceAddress: "",
        invoiceZip: "",
        invoiceCity: "",
        invoiceCountry: "PL"
      },
      // progi darmowej wysyłki
      freeShippingThresholds: {
        PLN: 200, EUR: 50, USD: 55, CZK: 1200, HUF: 20000, BGN: 100, RON: 250, DKK: 370, SEK: 550
      },
      defaultFreeShippingThreshold: 99999
    }
  },
  watch: {
    "form.country": {
      immediate: true,
      handler(newCountry) {
        this.fetchPaymentMethods(newCountry)
      }
    }
  },
  computed: {
    translations() { return translations.value },
    currentCurrency() { return currentCurrency.value || 'PLN' },

    total() {
      return this.cart.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0)
    },

    currentFreeShippingThreshold() {
      return this.freeShippingThresholds[this.currentCurrency] ?? this.defaultFreeShippingThreshold
    },
    freeShippingActive() {
      return this.total >= this.currentFreeShippingThreshold
    },

    // jedna stawka: najwyższa cena wysyłki (dla wybranego kraju), bez mnożenia przez ilość
    shippingTotal() {
      if (this.freeShippingActive) return 0
      let maxShip = 0
      for (const item of this.cart) {
        const opt = item.variant?.shipping_options?.find(o => o.country === this.form.country)
        if (!opt) continue
        let price = 0
        switch (this.currentCurrency) {
          case 'EUR': price = parseFloat(opt.price_eur) || 0; break
          case 'USD': price = parseFloat(opt.price_usd) || 0; break
          case 'CZK': price = parseFloat(opt.price_czk) || 0; break
          case 'HUF': price = parseFloat(opt.price_huf) || 0; break
          case 'BGN': price = parseFloat(opt.price_bgn) || 0; break
          case 'RON': price = parseFloat(opt.price_ron) || 0; break
          case 'DKK': price = parseFloat(opt.price_dkk) || 0; break
          case 'SEK': price = parseFloat(opt.price_sek) || 0; break
          default:    price = parseFloat(opt.price_pln) || 0; break
        }
        if (price > maxShip) maxShip = price
      }
      return maxShip
    },

    totalWithShipping() {
      return this.total + this.shippingTotal
    }
  },
  async mounted() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    window.addEventListener('languageChanged', this.reloadCartWithLang)
    this.unwatchCurrency = watch(
      () => this.currentCurrency,
      () => this.reloadCartWithLang()
    )
    await this.reloadCartWithLang()
  },
  beforeUnmount() {
    window.removeEventListener('languageChanged', this.reloadCartWithLang)
    if (this.unwatchCurrency) this.unwatchCurrency()
  },
  methods: {
    getVariantName(variant) {
      if (!variant) return ''
      const lang = this.currentLanguage || 'pl'
      return variant['value_'+lang] || variant.value || (variant.type && variant.type.name) || ''
    },
    regionToLang(code) {
      const m = {
        PL:'pl', DE:'de', AT:'de', BE:'nl', BG:'bg', HR:'hr', CZ:'cs', DK:'da', EE:'et',
        ES:'es', FR:'fr', NL:'nl', LT:'lt', LV:'lv', RO:'ro', SK:'sk', SI:'sl',
        SE:'sv', IT:'it', HU:'hu', PT:'pt', IE:'en'
      }
      return m[String(code).toUpperCase()] || 'en'
    },
    countryEndonym(iso) {
      const lang = this.regionToLang(iso)
      try {
        const dn = new Intl.DisplayNames([lang], { type: 'region' })
        return dn.of(String(iso).toUpperCase()) || String(iso).toUpperCase()
      } catch { return String(iso).toUpperCase() }
    },
    getPaymentMethodName(method) {
      const map = {
        1: 'cash_on_delivery',
        2: 'bank_transfer',
        3: 'card',
        4: 'blik',
        5: 'przelewy24',
        6: 'klarna',
        7: 'paypal',
      }
      const key = map[method.id]
      return (key && this.translations.payment?.[key]) || method.name
    },
    getCurrencySymbol,

    async reloadCartWithLang() {
      const raw = JSON.parse(localStorage.getItem("cart") || "[]")
      const currency = this.currentCurrency || 'PLN'
      const updatedCart = await Promise.all(
        raw.map(async (entry) => {
          try {
            const res = await fetch(`/api/v1/products/${entry.slug}/`, {
              headers: { 'Accept-Language': currentLang.value }
            })
            const product = await res.json()
            let variant = product.variants?.find(v => Number(v.id) === Number(entry.variantId))
            let price = parseFloat(product.price)
            if (variant?.prices?.length) {
              const found = variant.prices.find(p => p.currency?.code === currency)
              if (found) price = parseFloat(found.amount)
            }
            return {
              id: product.id,
              name: product.name,
              image: product.get_image || product.get_thumbnail,
              price,
              quantity: entry.quantity,
              variant
            }
          } catch {
            return null
          }
        })
      )
      this.cart = updatedCart.filter(Boolean)

      // odśwież listę krajów
      const countriesSet = new Set()
      this.cart.forEach(item => {
        item.variant?.shipping_options?.forEach(opt => {
          if (opt.country) {
            const code = String(opt.country).toUpperCase()
            countriesSet.add(JSON.stringify({ code: code, name: code }))
          }
        })
      })
      this.availableCountries = Array.from(countriesSet).map(j => JSON.parse(j))
      const storedCountry = localStorage.getItem('ship_to_iso')
      if (storedCountry && this.availableCountries.find(c => c.code === storedCountry)) {
        this.form.country = storedCountry
      } else if (!this.availableCountries.find(c => c.code === this.form.country)) {
        this.form.country = this.availableCountries[0]?.code || 'PL'
      }
    },

    async fetchPaymentMethods(country) {
      try {
        const res = await fetch(`/api/v1/payment-methods/?country=${country}`)
        const data = await res.json()
        this.availablePaymentMethods = data
      } catch (e) {
        console.error("Błąd ładowania metod płatności:", e)
        this.availablePaymentMethods = []
      }
    },

    trackPurchase(orderNumber) {
      try {
        const value = parseFloat(this.total.toFixed(2))
        const currency = currentCurrency.value
        const items = this.cart.map(item => ({ id: String(item.id), quantity: item.quantity, item_price: item.price }))
        const eventID = 'purchase_' + orderNumber
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Purchase', { value, currency, content_ids: items.map(i=>i.id), content_type: 'product', contents: items, order_id: orderNumber }, { eventID: eventID })
        }
        if (typeof window.gtag === 'function') {
          window.gtag('event', 'purchase', { transaction_id: orderNumber, value, currency, items: this.cart.map(item => ({ item_id: String(item.id), item_name: item.name, quantity: item.quantity, price: item.price })) })
        }
      } catch(e) { void e }
    },
    async order() {
      if (!this.cart.length) {
        alert("Koszyk jest pusty!")
        return
      }
      this.processing = true
      try {
        const orderPayload = {
          first_name: this.form.firstName,
          last_name: this.form.lastName,
          phone: this.form.phone,
          email: this.form.email,
          address: this.form.address,
          city: this.form.city,
          zip_code: this.form.zip,
          country: this.form.country,
          payment_method: this.form.paymentMethod,
          want_invoice: this.form.wantInvoice,
          invoice_company: this.form.invoiceCompany,
          invoice_nip: this.form.invoiceNIP,
          invoice_address: this.form.invoiceAddress,
          invoice_zip: this.form.invoiceZip,
          invoice_city: this.form.invoiceCity,
          invoice_country: this.form.invoiceCountry,
          total_price: this.total.toFixed(2),
          shipping_price: this.shippingTotal.toFixed(2),
          language: currentLang.value,
          currency: currentCurrency.value,
          items: this.cart.map(item => ({
            product_id: item.id,
            product_name: item.name,
            variant_id: item.variant?.id || null,
            variant_name: item.variant?.value || item.variant?.type?.name || '',
            price: item.price,
            quantity: item.quantity
          }))
        }

        // Mapowanie ID -> metoda
        const STRIPE_MAP = { 3: "card", 4: "blik", 5: "p24", 6: "klarna", 7: "paypal" }

        if (this.form.paymentMethod === 2) {
          const csrfToken = document.cookie.split("; ").find(r=>r.startsWith("csrftoken="))?.split("=")[1]||""
          const response = await fetch("/api/v1/orders/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(orderPayload)
          })
          if (!response.ok) throw new Error("Błąd zapisu zamówienia")
          const data = await response.json()
          this.trackPurchase(data.order_number)
          localStorage.removeItem("cart")
          this.cart = []
          this.$router.push({ path: "/potwierdzenie", query: { order: data.order_number } })
        } else if (Object.keys(STRIPE_MAP).includes(String(this.form.paymentMethod))) {
          const orderResponse = await fetch("/api/v1/orders/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(orderPayload)
          })
          if (!orderResponse.ok) throw new Error("Błąd zapisu zamówienia")
          const orderData = await orderResponse.json()

          const stripeResponse = await fetch("/api/v1/stripe/create/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              order_number: orderData.order_number,
              amount: parseFloat(orderPayload.total_price),
              currency: orderPayload.currency,
              method: STRIPE_MAP[this.form.paymentMethod]
            })
          })
          const stripeData = await stripeResponse.json()
          if (stripeData.url) {
            this.trackPurchase(orderData.order_number)
            window.location.href = stripeData.url
            localStorage.setItem("order_number", orderData.order_number)
          } else {
            throw new Error(stripeData.error || "Błąd podczas płatności Stripe")
          }
        } else {
          const csrfToken = document.cookie.split("; ").find(r=>r.startsWith("csrftoken="))?.split("=")[1]||""
          const response = await fetch("/api/v1/orders/", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(orderPayload)
          })
          if (!response.ok) throw new Error("Błąd zapisu zamówienia")
          const data = await response.json()
          this.trackPurchase(data.order_number)
          localStorage.removeItem("cart")
          this.cart = []
          this.$router.push({ path: "/potwierdzenie", query: { order: data.order_number } })
        }

      } catch (error) {
        alert("Błąd podczas składania zamówienia.")
        console.error(error)
      } finally {
        this.processing = false
      }
    }
  }
}
</script>

<style scoped>
/* tu możesz dodać style checkoutu jeśli potrzebne */
</style>
